/*
 * Copyright (c) 2024, King Living
 * Routes Configuration for Google Maps Integration
 */

import React from 'react';

import loadable from '@loadable/component';

import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config';
import {Skeleton} from '@salesforce/retail-react-app/app/components/shared/ui';
import {routes as baseRoutes} from '@salesforce/retail-react-app/app/routes';
import {configureRoutes} from '@salesforce/retail-react-app/app/utils/routes-utils';
import {PASSWORDLESS_LOGIN_LANDING_PATH, RESET_PASSWORD_LANDING_PATH} from '@amblique/king-living/app/constants';
import {SHOWROOMS_LISTING_PATH} from '@amblique/king-living/app/constants/site-config';
import AuthPageFallback from '@amblique/king-living/app/components/auth-page-fallback';

const fallback = <Skeleton height="75vh" width="100%" />;
const authFallback = <AuthPageFallback />;

const DynamicPage = loadable(() => import('./pages/dynamic-page'), {fallback});

// URL-scoped Contentful preview (KL-302). Dedicated route so DynamicPage stays a pure
// global-URL-scope resolver. See docs/contentful/preview/README.md §5.1.
const PreviewPage = loadable(() => import('./pages/preview'), {fallback});

// Import store pages with lazy loading
const StoreLocatorPage = loadable(() => import('./pages/stores/index'), {
  fallback,
});

const TestGeocodingPage = loadable(() => import('./pages/test-geocoding/index'), {
  fallback,
});

const StylingCheckPage = loadable(() => import('./pages/styling-check'), {
  fallback,
});

// Auth pages with CRM integration
const Registration = loadable(() => import('./pages/registration'), {
  fallback: authFallback,
});

const Login = loadable(() => import('./pages/login'), {
  fallback: authFallback,
});

const ResetPassword = loadable(() => import('./pages/reset-password'), {
  fallback: authFallback,
});

const JobDetailsPage = loadable(() => import('./pages/careers/job-details'), {
  fallback,
});

const AddToCartUpsell = loadable(() => import('./pages/add-to-cart-upsell'), {fallback});

const PlannersPage = loadable(() => import('./pages/planners'), {fallback});

const CheckoutContainer = loadable(() => import('./pages/checkout/index'), {fallback});
// KINDW-270 / KINDW-270.22 — Stripe BNPL / 3DS return-back landing.
// See `pages/checkout/payment-processing.jsx` + docs/payments/stripe/03-payment-flow.md §3.2.
const CheckoutPaymentProcessing = loadable(
  () => import('@amblique/king-living/app/pages/checkout/payment-processing'),
  {fallback}
);

// Define custom routes
// Routes are matched in order - custom routes come BEFORE baseRoutes to override base pages
export const routes = [
  // Auth pages with CRM integration (override base)
  {
    path: '/registration',
    component: Registration,
    exact: true,
  },
  {
    path: '/login',
    component: Login,
    exact: true,
  },
  {
    path: '/reset-password',
    component: ResetPassword,
    exact: true,
  },
  {
    path: RESET_PASSWORD_LANDING_PATH,
    component: ResetPassword,
    exact: true,
  },
  {
    path: PASSWORDLESS_LOGIN_LANDING_PATH,
    component: Login,
    exact: true,
  },

  // Custom pages
  {
    path: SHOWROOMS_LISTING_PATH,
    component: StoreLocatorPage,
    exact: true,
  },
  {
    path: '/test-geocoding', // TODO: Remove this route after testing
    component: TestGeocodingPage,
    exact: true,
  },
  {
    path: '/styling-check',
    component: StylingCheckPage,
    exact: true,
  },
  // Job detail page — standalone, not Contentful-driven (JA-005, AC-2)
  {
    path: '/careers/:jobId',
    component: JobDetailsPage,
    exact: true,
  },
  {
    path: '/cart/upsell',
    component: AddToCartUpsell,
    exact: true,
  },
  {
    path: '/planners',
    component: PlannersPage,
    exact: true,
  },

  // Shared payment return landing for Stripe (`return_url`, KINDW-270.22) and
  // PayPal (`token`, KINDW-276). Must precede baseRoutes so the redirect-back
  // path resolves before the generic /checkout or any catch-all.
  // See [docs/payments/stripe/03-payment-flow.md] §3.2.
  {
    path: '/checkout/payment-processing',
    component: CheckoutPaymentProcessing,
    exact: true,
  },
  // PayPal billing cancel alias (before generic /checkout from baseRoutes)
  {
    path: '/checkout/billing',
    component: CheckoutContainer,
    exact: true,
  },
  {
    path: '/checkout',
    component: CheckoutContainer,
    exact: true,
  },

  // URL-scoped Contentful preview: `/{site}/preview/<slug-or-entryId>` renders drafts.
  // `:slugOrId(.*)` captures the whole tail (multi-segment slugs, and the `//` that
  // Contentful's `{locale}/preview/{slug}` template can emit); PreviewPage derives the
  // real slug/id from the pathname. Must precede the catch-all `*`.
  {
    path: '/preview/:slugOrId(.*)',
    component: PreviewPage,
  },

  // Contentful dynamic pages: /page, /page/blog/..., etc. (before baseRoutes so /page/* hits DynamicPage)
  {
    path: '/page',
    component: DynamicPage,
  },

  // Base routes (custom routes above take precedence), excluding:
  //   '/'    → handled by DynamicPage
  //   '/cart' → removed; cart is accessed via the flyout only
  //   '/product/:productId' → PDPs are Contentful-only (slug via DynamicPage)
  //   '*'    → catch-all handled by DynamicPage below
  ...baseRoutes.filter(
    (r) =>
      r.path !== '*' &&
      r.path !== '/' &&
      r.path !== '/cart' &&
      r.path !== '/product/:productId' &&
      r.path !== '/checkout' &&
      r.path !== '/checkout/billing' &&
      r.path !== '/checkout/payment-processing'
  ),

  // Catch-all: Dynamic Page Routing per TSD Contentful (Contentful → category → NotFound)
  {
    path: '*',
    component: DynamicPage,
  }
];

// Export routes function for PWA Kit
export default () => {
  const config = getConfig();

  return configureRoutes(routes, config, {
    ignoredRoutes: ['/callback', '/store-locator', '*'],
  });
};
